/**
 * ConfirmDeliveryPage — driver finalises a delivery with the receiver.
 * Mirrors Flutter `delivery/delivery_confirmation_page.dart`.
 *
 * The confirmation receipt has 4 elements (any combination satisfies the
 * server depending on shipment policy; OTP is the strongest signal):
 *   1. OTP — receiver gets a 6-digit code via WhatsApp/SMS, gives it to
 *      the driver. Tap "إرسال الكود للمستلم" to trigger the WhatsApp send.
 *   2. Photo of delivery — proof of handover, stored in raw column.
 *   3. Receiver signature — captured as a URL today (canvas in Phase 7).
 *   4. Receiver ID photo — for high-value or contract shipments.
 *
 * Server endpoints used:
 *   POST /api/cargo/shipments/:id/delivery-otp     — request server to SMS/WA
 *   POST /api/cargo/shipments/:id/confirm-delivery — submit the receipt
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, CheckCircle, MessageCircle, Camera, FileSignature,
  AlertCircle, Loader, Hash,
} from 'lucide-react';
import { useShipment, useGenerateDeliveryOtp, useConfirmDelivery } from '@/hooks/useShipments';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';

export default function ConfirmDeliveryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const idNum = id ? parseInt(id, 10) : null;
  const { data: shipment, isLoading } = useShipment(idNum);
  const sendOtp = useGenerateDeliveryOtp();
  const confirm = useConfirmDelivery();

  const [otpCode, setOtpCode] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [signatureUrl, setSignatureUrl] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (isLoading || !idNum) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }
  if (!shipment) {
    return (
      <AppLayout>
        <div className="bg-error/10 text-error rounded-xl p-4">
          {t('shipment_not_found', 'الشحنة غير موجودة')}
        </div>
      </AppLayout>
    );
  }

  async function handleSendOtp() {
    setError(null);
    try {
      await sendOtp.mutateAsync(idNum!);
      setOtpSent(true);
    } catch (e: any) {
      setError(e?.message || t('otp_send_failed', 'فشل إرسال الكود'));
    }
  }

  async function handleConfirm() {
    setError(null);
    if (!otpCode.trim() && !photoUrl.trim()) {
      setError(t('delivery_proof_required', 'يجب إدخال OTP أو رابط صورة على الأقل'));
      return;
    }
    try {
      await confirm.mutateAsync({
        id: idNum!,
        otpCode: otpCode.trim() || undefined,
        photoUrl: photoUrl.trim() || undefined,
        signatureUrl: signatureUrl.trim() || undefined,
      });
      setSuccess(true);
      setTimeout(() => navigate(`/shipments/${idNum}`, { replace: true }), 1500);
    } catch (e: any) {
      setError(e?.message || t('confirm_delivery_failed', 'فشل تأكيد التسليم'));
    }
  }

  if (success) {
    return (
      <AppLayout>
        <div className="bg-success/10 border border-success/30 rounded-2xl p-8 text-center max-w-md mx-auto">
          <CheckCircle size={56} className="mx-auto text-success mb-3" />
          <h2 className="text-xl font-bold text-success mb-1">
            {t('delivery_confirmed', 'تم تأكيد التسليم')}
          </h2>
          <p className="text-text-secondary text-sm">
            {t('delivery_confirmed_hint', 'سيتم تحويل أرباحك خلال ٢٤ ساعة')}
          </p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary">
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="bg-surface border border-border rounded-2xl p-6 max-w-xl mx-auto">
        <h1 className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
          <CheckCircle size={20} /> {t('confirm_delivery', 'تأكيد التسليم')}
        </h1>
        <div className="flex items-center gap-3 text-text-secondary text-sm mb-5">
          <span className="font-mono flex items-center gap-1"><Hash size={12} /> {shipment.tracking_number}</span>
          <span>· {shipment.receiver_name}</span>
        </div>

        {error && (
          <div className="bg-error/10 text-error rounded-xl p-3 text-sm mb-4 flex items-center gap-2">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <p className="text-text-secondary text-sm mb-5">
          {t('confirm_delivery_intro',
             'لتأكيد التسليم، أدخل OTP الذي يصل المستلم على الواتساب، أو ارفع صورة الإيصال.')}
        </p>

        {/* Step 1: OTP */}
        <Step n={1} title={t('step_otp', 'كود OTP من المستلم')}>
          <div className="flex items-end gap-2 mb-2">
            <Button variant="ghost" onClick={handleSendOtp} loading={sendOtp.isPending}>
              <MessageCircle size={14} /> {otpSent ? t('resend_otp', 'إعادة إرسال') : t('send_otp_to_receiver', 'إرسال الكود للمستلم')}
            </Button>
            {otpSent && <span className="text-xs text-success">✓ {t('otp_sent', 'أُرسِل')}</span>}
          </div>
          <TextField
            type="text"
            inputMode="numeric"
            dir="ltr"
            placeholder="123456"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
            maxLength={8}
            className="font-mono text-lg tracking-widest text-center"
          />
        </Step>

        {/* Step 2: Photo */}
        <Step n={2} title={t('step_photo', 'صورة الإيصال (اختياري)')}>
          <p className="text-xs text-text-tertiary mb-2">
            <Camera size={12} className="inline" />{' '}
            {t('photo_hint', 'صوّر الطرد عند التسليم وضع رابط الصورة هنا')}
          </p>
          <TextField
            dir="ltr"
            placeholder="https://…"
            value={photoUrl}
            onChange={(e) => setPhotoUrl(e.target.value)}
          />
        </Step>

        {/* Step 3: Signature */}
        <Step n={3} title={t('step_signature', 'توقيع المستلم (اختياري)')}>
          <p className="text-xs text-text-tertiary mb-2">
            <FileSignature size={12} className="inline" />{' '}
            {t('signature_hint', 'صوّر التوقيع وضع الرابط (لوحة الرسم تأتي قريباً)')}
          </p>
          <TextField
            dir="ltr"
            placeholder="https://…"
            value={signatureUrl}
            onChange={(e) => setSignatureUrl(e.target.value)}
          />
        </Step>

        <Button onClick={handleConfirm} loading={confirm.isPending} fullWidth className="mt-4">
          <CheckCircle size={16} /> {t('finalize_delivery', 'إغلاق التسليم')}
        </Button>
      </div>
    </AppLayout>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-border pt-4 mt-4 first:border-t-0 first:pt-0 first:mt-0">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-6 h-6 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center">
          {n}
        </span>
        <h3 className="font-semibold text-text-primary">{title}</h3>
      </div>
      <div className="ms-8">{children}</div>
    </div>
  );
}
